import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Loader2, Mic, CalendarPlus, Download, Bell, BellRing, Pencil, Trash2, Plus, GripVertical, Image as ImageIcon, Upload, AlertTriangle, Radio, PhoneCall } from "lucide-react";
import api from "@/lib/api";
import { googleCalendarUrl, downloadIcs } from "@/lib/calendar";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { fmtDateTime } from "@/lib/date";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const emptyShow = { title: "", host: "", station_id: "", day: "Monday", start_time: "", end_time: "", description: "", takes_calls: false };
const COVER_PRESETS = [
  "https://images.unsplash.com/photo-1579975096649-e773152b04cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1507692049790-de58290a4334?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1522158637959-30385a09e0da?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1570786032462-2efc3ca8fccd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

export default function Schedule() {
  const { user } = useAuth();
  const { playStation } = usePlayer();
  const [programs, setPrograms] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveShows, setLiveShows] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [reminders, setReminders] = useState(new Set());
  const [showForm, setShowForm] = useState(null);
  const [savingShow, setSavingShow] = useState(false);
  const [drag, setDrag] = useState(null); // { p, startX, startY, dx, dy }
  const [uploadingCover, setUploadingCover] = useState(false);
  const [myShowsOnly, setMyShowsOnly] = useState(false);
  const coverInputRef = React.useRef(null);
  const todayName = DAYS[new Date().getDay()];
  const [activeDay, setActiveDay] = useState(todayName);
  const canManage = user && (user.role === "host" || user.role === "admin");
  const canManageShow = (p) => user && (user.role === "admin" || (p.owner_id && p.owner_id === user.id) || (p.host_id && p.host_id === user.id));

  const loadPrograms = () => api.get("/programs").then((res) => setPrograms(res.data)).catch(() => {});

  const tuneIn = (p) => {
    const st = stations.find((s) => s.id === p.station_id);
    if (!st) { toast.error("Station is no longer available"); return; }
    playStation(st);
    toast.success(`Tuning into ${st.name}`);
  };

  const moveShowToDay = async (p, newDay) => {
    if (!newDay || newDay === p.day) return;
    try {
      await api.put(`/programs/${p.id}`, { title: p.title, host: p.host, host_id: p.host_id, station_id: p.station_id, image_url: p.image_url, day: newDay, start_time: p.start_time, end_time: p.end_time, description: p.description, takes_calls: p.takes_calls });
      const clash = findConflict(newDay, p.start_time, p.end_time, p.id);
      if (clash) toast.warning(`Moved to ${newDay}, but it overlaps "${clash.title}" (${clash.start_time}–${clash.end_time})`);
      else toast.success(`Moved "${p.title}" to ${newDay}`);
      loadPrograms();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not move show"); }
  };

  const onGripDown = (p) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    setDrag({ p, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, dx: 0, dy: 0 });
  };
  const onGripMove = (e) => {
    setDrag((d) => (d ? { ...d, dx: e.clientX - d.startX, dy: e.clientY - d.startY } : d));
  };
  const reorderWithinDay = async (draggedId, overId) => {
    const ids = dayPrograms.map((p) => p.id);
    const from = ids.indexOf(draggedId), to = ids.indexOf(overId);
    if (from < 0 || to < 0 || from === to) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setPrograms((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, order: ids.indexOf(p.id) } : p)));
    try { await api.put("/programs/reorder", { ids }); } catch (e) { toast.error("Could not reorder"); loadPrograms(); }
  };

  const onGripUp = (e) => {
    setDrag((d) => {
      if (d) {
        let dayTarget = null, overCardId = null;
        document.querySelectorAll("[data-day-tab]").forEach((t) => {
          const r = t.getBoundingClientRect();
          if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) dayTarget = t.getAttribute("data-day-tab");
        });
        if (dayTarget) {
          moveShowToDay(d.p, dayTarget);
        } else {
          document.querySelectorAll("[data-testid^='show-card-']").forEach((c) => {
            const id = c.getAttribute("data-testid").replace("show-card-", "");
            if (id === d.p.id) return;
            const r = c.getBoundingClientRect();
            if (e.clientY >= r.top && e.clientY <= r.bottom) overCardId = id;
          });
          if (overCardId) reorderWithinDay(d.p.id, overCardId);
        }
      }
      return null;
    });
  };

  useEffect(() => {
    api.get("/programs").then((res) => { setPrograms(res.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/stations").then((res) => setStations(res.data)).catch(() => {});
    api.get("/live/schedule").then((res) => setLiveShows(res.data)).catch(() => {});
    const t = setInterval(() => { if (!drag) setNow(Date.now()); }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!user) { setReminders(new Set()); return; }
    api.get("/me/program-reminders").then((res) => setReminders(new Set(res.data.program_ids || []))).catch(() => {});
  }, [user]);

  const toMinutes = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
  const findConflict = (day, start, end, excludeId) => {
    const s = toMinutes(start);
    const e = end && end !== "00:00" ? toMinutes(end) : 24 * 60;
    return programs.find((p) => {
      if (p.id === excludeId || p.day !== day || !p.start_time) return false;
      const ps = toMinutes(p.start_time);
      const pe = p.end_time && p.end_time !== "00:00" ? toMinutes(p.end_time) : 24 * 60;
      return s < pe && ps < e; // ranges overlap
    });
  };

  const toHHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  const nearestFreeSlot = (day, start, end, excludeId) => {
    const s = toMinutes(start);
    const dur = Math.max(15, (end && end !== "00:00" ? toMinutes(end) : s + 60) - s);
    const intervals = programs
      .filter((p) => p.id !== excludeId && p.day === day && p.start_time)
      .map((p) => [toMinutes(p.start_time), p.end_time && p.end_time !== "00:00" ? toMinutes(p.end_time) : 24 * 60])
      .sort((a, b) => a[0] - b[0]);
    const candidates = [0, ...intervals.map((iv) => iv[1])].sort((a, b) => a - b);
    for (const c of candidates) {
      if (c + dur > 24 * 60) continue;
      const clashes = intervals.some(([ps, pe]) => c < pe && ps < c + dur);
      if (!clashes) return { start: toHHMM(c), end: toHHMM(c + dur) };
    }
    return null;
  };
  const applyNearestSlot = () => {
    const slot = nearestFreeSlot(showForm.day, showForm.start_time, showForm.end_time, showForm.id);
    if (slot) setShowForm((f) => ({ ...f, start_time: slot.start, end_time: slot.end }));
    else toast.error("No free slot found on this day");
  };

  const saveShow = async () => {
    if (!showForm.title.trim() || !showForm.start_time) { toast.error("Title and start time are required"); return; }
    const { id, ...body } = showForm;
    setSavingShow(true);
    try {
      if (id) { await api.put(`/programs/${id}`, body); toast.success("Show updated"); }
      else { await api.post("/programs", body); toast.success("Show added"); }
      setShowForm(null); loadPrograms();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save show"); } finally { setSavingShow(false); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/programs/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShowForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Cover uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingCover(false); if (coverInputRef.current) coverInputRef.current.value = ""; }
  };

  const delShow = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This removes it from the schedule for everyone.`)) return;
    try { await api.delete(`/programs/${p.id}`); toast.success("Show deleted"); loadPrograms(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const toggleReminder = async (p) => {
    if (!user) { toast.error("Sign in to get show reminders"); return; }
    try {
      const res = await api.post(`/programs/${p.id}/remind`);
      setReminders((prev) => {
        const next = new Set(prev);
        if (res.data.reminded) next.add(p.id); else next.delete(p.id);
        return next;
      });
      toast.success(res.data.reminded ? `We'll remind you before "${p.title}" 🔔` : "Reminder removed");
    } catch (e) { toast.error("Could not update reminder"); }
  };

  const countdown = (iso) => {
    const diff = new Date(iso).getTime() - now;
    if (diff <= 0) return "Starting now";
    const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  };

  const dayPrograms = programs
    .filter((p) => p.day === activeDay)
    .filter((p) => !myShowsOnly || canManageShow(p))
    .sort((a, b) => {
      const ao = typeof a.order === "number" ? a.order : 999, bo = typeof b.order === "number" ? b.order : 999;
      if (ao !== bo) return ao - bo;
      return a.start_time.localeCompare(b.start_time);
    });

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  const isLive = (p) => activeDay === todayName && nowMin >= toMin(p.start_time) && (nowMin < toMin(p.end_time) || p.end_time === "00:00");

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-3">
          <CalendarClock className="w-5 h-5 text-[#E6B34A]" />
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Program Guide</h1>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <button onClick={() => setMyShowsOnly((v) => !v)} data-testid="my-shows-toggle" className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${myShowsOnly ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
              <Mic className="w-4 h-4" /> {myShowsOnly ? "My shows" : "All shows"}
            </button>
            <button onClick={() => setShowForm({ ...emptyShow, day: activeDay })} data-testid="add-show" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors">
              <Plus className="w-4 h-4" /> Add show
            </button>
          </div>
        )}
      </div>
      <p className="text-zinc-400 mb-8">Weekly line-up of shows and hosts.</p>

      {liveShows.length > 0 && (
        <div className="mb-10" data-testid="upcoming-live">
          <h2 className="font-display font-extrabold text-lg lg:text-xl tracking-tight mb-4 flex items-center gap-2"><Mic className="w-5 h-5 text-[#E6B34A]" /> Upcoming Live Shows</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {liveShows.map((s) => (
              <div key={s.id} className="rounded-2xl border border-[#E6B34A]/25 bg-[#141C42] overflow-hidden" data-testid={`live-sched-${s.id}`}>
                {s.has_cover && (
                  <img src={`${api.defaults.baseURL}${s.cover_url}`} alt={s.title} data-testid={`sched-cover-${s.id}`} className="w-full h-40 object-cover" />
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="overline text-[#E6B34A]">Live Show</span>
                    <span className="text-sm font-mono text-[#E6B34A]" data-testid={`countdown-${s.id}`}>{countdown(s.scheduled_at)}</span>
                  </div>
                  <h3 className="font-display font-extrabold text-xl tracking-tight mt-1.5">{s.title}</h3>
                  <p className="text-sm text-zinc-400 mt-0.5">with {s.host_name} · {fmtDateTime(s.scheduled_at)}</p>
                  {s.description && <p className="text-sm text-zinc-500 mt-2">{s.description}</p>}
                  <div className="flex items-center gap-2 mt-4">
                    <a href={googleCalendarUrl({ title: s.title, starts_at: s.scheduled_at, description: s.description })} target="_blank" rel="noreferrer" data-testid={`cal-google-${s.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors">
                      <CalendarPlus className="w-3.5 h-3.5" /> Add to calendar
                    </a>
                    <button onClick={() => downloadIcs({ id: s.id, title: s.title, starts_at: s.scheduled_at, description: s.description })} data-testid={`cal-ics-${s.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs font-semibold hover:bg-white/10 transition-colors">
                      <Download className="w-3.5 h-3.5" /> .ics
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="day-tabs">
        {DAYS.map((d) => (
          <button
            key={d}
            onClick={() => setActiveDay(d)}
            data-testid={`day-${d}`}
            data-day-tab={d}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              activeDay === d ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-400 hover:text-white"
            }`}
          >
            {d.slice(0, 3)}
          </button>
        ))}
      </div>
      {canManage && (
        <p className="text-xs text-zinc-500 mb-6 flex items-center gap-1.5" data-testid="reschedule-hint">
          <GripVertical className="w-3.5 h-3.5" /> Tip: drag one of your shows onto a day above to reschedule it.
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>
      ) : dayPrograms.length === 0 ? (
        <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl">No programs scheduled for {activeDay}.</p>
      ) : (
        <div className="border-l-2 border-white/10 ml-2 space-y-6" data-testid="schedule-list">
          {dayPrograms.map((p) => {
            const manage = canManageShow(p);
            return (
            <div key={p.id} className="relative pl-8">
              <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full ${isLive(p) ? "bg-[#E6B34A] shadow-[0_0_12px_rgba(230,179,74,0.7)]" : "bg-zinc-600"}`} />
              <div
                data-testid={`show-card-${p.id}`}
                style={drag && drag.p.id === p.id ? { transform: `translate(${drag.dx}px, ${drag.dy}px) scale(1.02)`, zIndex: 50, position: "relative", boxShadow: "0 12px 40px rgba(0,0,0,0.5)", touchAction: "none" } : undefined}
                className={`rounded-2xl border overflow-hidden select-none ${isLive(p) ? "border-[#E6B34A]/40 bg-[#E6B34A]/5" : "border-white/5 bg-[#141C42]"}`}
              >
                {p.image_url && (
                  <img src={p.image_url} alt={p.title} draggable={false} style={{ WebkitUserDrag: "none" }} data-testid={`show-cover-${p.id}`} className="w-full h-36 object-cover pointer-events-none" />
                )}
                <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-2">
                    {manage && <GripVertical onPointerDown={onGripDown(p)} onPointerMove={onGripMove} onPointerUp={onGripUp} className="w-5 h-5 text-zinc-500 mt-1 shrink-0 cursor-grab active:cursor-grabbing touch-none" data-testid={`show-drag-${p.id}`} />}
                    <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-sm text-[#E6B34A]">{p.start_time} – {p.end_time}</span>
                      {isLive(p) && (
                        <span className="flex items-center gap-1 text-[#FF3B30]"><span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] live-dot" /><span className="overline">On Air</span></span>
                      )}
                    </div>
                    <h3 className="font-display font-extrabold text-xl tracking-tight mt-1.5">{p.title}</h3>
                    {p.host && (
                      <p className="text-sm text-zinc-400 mt-0.5">
                        with{" "}
                        {p.host_id ? (
                          <Link to={`/hosts/${p.host_id}`} data-testid={`program-host-link-${p.id}`} className="text-[#E6B34A] font-semibold hover:underline">{p.host}</Link>
                        ) : (
                          <span>{p.host}</span>
                        )}
                      </p>
                    )}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5">
                    {p.station_id && (
                      <button onClick={() => tuneIn(p)} data-testid={`tune-in-${p.id}`} title="Tune into this show's station"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#E6B34A]/15 text-[#E6B34A] hover:bg-[#E6B34A]/25 transition-colors">
                        <Radio className="w-3.5 h-3.5" /><span className="hidden sm:inline">Tune in</span>
                      </button>
                    )}
                    <button
                      onClick={() => toggleReminder(p)}
                      data-testid={`remind-${p.id}`}
                      title={reminders.has(p.id) ? "Reminder on — tap to turn off" : "Remind me before this show"}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${reminders.has(p.id) ? "bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
                    >
                      {reminders.has(p.id) ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
                      <span className="hidden sm:inline">{reminders.has(p.id) ? "Reminder on" : "Remind me"}</span>
                    </button>
                    {manage && (
                      <>
                        <button onClick={() => setShowForm({ ...p })} data-testid={`edit-show-${p.id}`} title="Edit show" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:text-[#E6B34A] hover:bg-white/10 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => delShow(p)} data-testid={`delete-show-${p.id}`} title="Delete show" className="p-2 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </div>
                {p.description && <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{p.description}</p>}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!showForm} onOpenChange={(o) => !o && setShowForm(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="show-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">{showForm?.id ? "Edit Show" : "New Show"}</DialogTitle></DialogHeader>
          {showForm && (
            <div className="space-y-3 mt-2">
              <div><label className="overline text-zinc-400">Title</label><Input value={showForm.title} onChange={(e) => setShowForm({ ...showForm, title: e.target.value })} data-testid="sf-title" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div><label className="overline text-zinc-400">Host</label><Input value={showForm.host} onChange={(e) => setShowForm({ ...showForm, host: e.target.value })} data-testid="sf-host" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div>
                <label className="overline text-zinc-400">Station to play</label>
                <select value={showForm.station_id || ""} onChange={(e) => setShowForm({ ...showForm, station_id: e.target.value })} data-testid="sf-station" className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  <option value="">— No station —</option>
                  {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">Listeners can tap the show to tune into this station.</p>
              </div>
              <div>
                <label className="overline text-zinc-400">Day</label>
                <select value={showForm.day} onChange={(e) => setShowForm({ ...showForm, day: e.target.value })} data-testid="sf-day" className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">Start</label><Input type="time" value={showForm.start_time} onChange={(e) => setShowForm({ ...showForm, start_time: e.target.value })} data-testid="sf-start" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">End</label><Input type="time" value={showForm.end_time} onChange={(e) => setShowForm({ ...showForm, end_time: e.target.value })} data-testid="sf-end" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <div><label className="overline text-zinc-400">Description</label><Textarea value={showForm.description} onChange={(e) => setShowForm({ ...showForm, description: e.target.value })} data-testid="sf-desc" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2.5" data-testid="sf-takes-calls-row">
                <input type="checkbox" checked={!!showForm.takes_calls} onChange={(e) => setShowForm({ ...showForm, takes_calls: e.target.checked })} data-testid="sf-takes-calls" className="accent-[#E6B34A] w-4 h-4" />
                <PhoneCall className="w-4 h-4 text-[#E6B34A]" /> This show takes calls <span className="text-[11px] text-zinc-500">(auto-opens the call-in line during this show)</span>
              </label>
              <div>
                <label className="overline text-zinc-400">Cover image</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input value={showForm.image_url || ""} onChange={(e) => setShowForm({ ...showForm, image_url: e.target.value })} data-testid="sf-image" placeholder="Paste a URL or upload…" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
                  <input ref={coverInputRef} type="file" accept="image/*" onChange={uploadCover} className="hidden" data-testid="sf-cover-file" />
                  <button type="button" onClick={() => coverInputRef.current && coverInputRef.current.click()} disabled={uploadingCover} data-testid="sf-cover-upload" className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 text-zinc-200 text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-60">
                    {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                  </button>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-zinc-500 mb-1.5">Or pick a ready-made cover:</p>
                <div className="flex gap-2 flex-wrap" data-testid="cover-library">
                  {COVER_PRESETS.map((url, i) => (
                    <button key={i} type="button" onClick={() => setShowForm((f) => ({ ...f, image_url: url }))} data-testid={`cover-preset-${i}`} className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-colors ${showForm.image_url === url ? "border-[#E6B34A]" : "border-transparent hover:border-white/30"}`}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
              {showForm.image_url ? (
                <img src={showForm.image_url} alt="" data-testid="sf-cover-preview" className="w-full h-32 object-cover rounded-xl border border-white/10" />
              ) : (
                <div className="w-full h-16 rounded-xl border border-dashed border-white/10 flex items-center justify-center text-zinc-600 text-xs gap-2"><ImageIcon className="w-4 h-4" /> Upload from your device or paste a link for cover art</div>
              )}
              {showForm.start_time && findConflict(showForm.day, showForm.start_time, showForm.end_time, showForm.id) && (
                <div className="rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 text-sm text-[#FFB3AD]" data-testid="conflict-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Heads up — this overlaps <span className="font-bold">{findConflict(showForm.day, showForm.start_time, showForm.end_time, showForm.id).title}</span> ({findConflict(showForm.day, showForm.start_time, showForm.end_time, showForm.id).start_time}–{findConflict(showForm.day, showForm.start_time, showForm.end_time, showForm.id).end_time}). You can still save it.</span>
                  </div>
                  {nearestFreeSlot(showForm.day, showForm.start_time, showForm.end_time, showForm.id) && (
                    <button type="button" onClick={applyNearestSlot} data-testid="use-free-slot" className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors">
                      Use nearest free time ({nearestFreeSlot(showForm.day, showForm.start_time, showForm.end_time, showForm.id).start}–{nearestFreeSlot(showForm.day, showForm.start_time, showForm.end_time, showForm.id).end})
                    </button>
                  )}
                </div>
              )}
              <button onClick={saveShow} disabled={savingShow} data-testid="save-show" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {savingShow && <Loader2 className="w-4 h-4 animate-spin" />}{showForm.id ? "Save Changes" : "Add Show"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
